import axios, { AxiosResponse } from 'axios';

import { interceptUnAuthenticatedError } from '../interceptUnAuthenticatedError';

import { getAccessToken } from '@utils/accessToken';

import { LogLevel, RecordList } from '@domui-domain/type';
import i18n from '@i18n';

import { log } from '@hooks/logger';

export interface DominoResponseData {
  wfRes: {
    statusText: string;
    responseData: {
      status: number;
      detail: string;
      errors?: unknown[];
    };
  };
}

export type DominoAxiosResponse = AxiosResponse<DominoResponseData>;
export type DominoError = Error | DominoAxiosResponse;
let reactErrorHandler: ((error: DominoError) => void) | null = null;

class Workflow {
  workflow!: string;

  action!: string;

  directBaseUrl!: string;

  directEndPoint!: string;

  internalCall!: boolean;

  baseUrl = `${process.env.GATSBY_MRX_API_PUBLIC_URL}/domui`;

  data!: RecordList;

  constructor(workflow: string) {
    this.internalCall = false;
    this.workflow = workflow;
  }

  static setReactErrorHandler(handler: (error: DominoError) => void) {
    reactErrorHandler = handler;
  }

  static forwardToReact(error: DominoError) {
    if (reactErrorHandler) {
      if (axios.isAxiosError(error) && error.response) {
        reactErrorHandler(error.response);
      } else {
        reactErrorHandler(error);
      }
    }
  }

  setInternalCall() {
    this.internalCall = true;
  }

  setAction(action: string) {
    this.action = action;
  }

  setData(data: RecordList) {
    this.data = this.internalCall ? { ...data, callType: 'internal' } : data;
  }

  getUrl() {
    return `${this.baseUrl}/${this.workflow}/${this.action}`;
  }

  // Attaches the shared Auth0/Okta access token (same one propagated to the
  // GraphQL layer) to direct Domino workflow calls so the domui BFF route
  // can forward it downstream alongside the existing session cookie.
  // eslint-disable-next-line class-methods-use-this
  getAuthHeaders() {
    const accessToken = getAccessToken();

    return {
      'Language-Context': i18n.language,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
  }

  logError = (e: unknown) => {
    const isProd = process.env.GATSBY_ENV === 'prod';
    const baseMessage = `DOMINO: UI error in workflow "${this.workflow}" and action "${this.action}"`;
    let logMessage = baseMessage;
    let other: Record<string, unknown> = !isProd ? { ...this.data } : {};

    if (typeof e === 'string') {
      logMessage += `: ${e}`;
    } else if (e instanceof Error) {
      logMessage += `: ${e.message}`;
      other = {
        ...other,
        name: e.name,
        stack: isProd ? undefined : e.stack,
        message: e.message,
      };
    } else {
      logMessage += ': Unknown error object';
      other = {
        ...other,
        rawError: !isProd ? e : undefined,
      };
    }

    log({
      level: LogLevel.Error,
      message: logMessage,
      other,
    });

    if (e instanceof Error) {
      Workflow.forwardToReact(e);
    }
  };

  async getCall() {
    try {
      const res = await axios.get(this.getUrl(), {
        params: this.data,
        withCredentials: true,
        headers: this.getAuthHeaders(),
      });
      return res;
    } catch (err) {
      this.logError(err);
      interceptUnAuthenticatedError(err);

      if (axios.isAxiosError(err)) {
        if (err?.response?.status === 404) return err.response;
        if (err.response) {
          throw new Error(
            `Error ${err.response.status}: ${err.response?.data?.wfRes
              ?.message || err.response?.statusText}`
          );
        } else if (err.request) {
          throw new Error(
            'No response received from the server. Please check your network.'
          );
        } else {
          throw new Error('Error in request setup.');
        }
      } else {
        throw new Error('An unexpected error occurred.');
      }
    }
  }

  async postCall() {
    try {
      const res = await axios.post(
        this.getUrl(),
        { data: { ...this.data, apiMethod: 'post' } },
        {
          withCredentials: true,
          headers: this.getAuthHeaders(),
        }
      );
      return res;
    } catch (err) {
      this.logError(err);
      interceptUnAuthenticatedError(err);

      if (axios.isAxiosError(err)) {
        if (err?.response?.status === 404) return err.response;
        if (err.response) {
          const typedError = err as RecordList;
          throw typedError.response?.data?.wfRes?.responseData?.errors?.[0] ||
            typedError.response?.data?.wfRes?.responseData ||
            typedError.response?.data?.wfRes?.responseData?.errorResult ||
            new Error('An unexpected error occurred.');
        } else if (err.request) {
          throw new Error(
            'No response received from the server. Please check your network.'
          );
        } else {
          throw new Error('Error in request setup.');
        }
      } else {
        throw new Error('An unexpected error occurred.');
      }
    }
  }

  async putCall() {
    try {
      const res = await axios.post(
        this.getUrl(),
        { data: { ...this.data, apiMethod: 'put' } },
        {
          withCredentials: true,
          headers: this.getAuthHeaders(),
        }
      );
      return res;
    } catch (err) {
      this.logError(err);
      interceptUnAuthenticatedError(err);

      if (axios.isAxiosError(err)) {
        if (err.response) {
          const typedError = err as RecordList;
          throw typedError.response?.data?.wfRes?.responseData?.errors?.[0] ||
            typedError.response?.data?.wfRes?.responseData ||
            new Error('An unexpected error occurred.');
        } else if (err.request) {
          throw new Error(
            'No response received from the server. Please check your network.'
          );
        } else {
          throw new Error('Error in request setup.');
        }
      } else {
        throw new Error('An unexpected error occurred.');
      }
    }
  }

  async deleteCall() {
    try {
      const res = await axios.post(
        this.getUrl(),
        { data: { ...this.data, apiMethod: 'delete' } },
        {
          withCredentials: true,
          headers: this.getAuthHeaders(),
        }
      );
      return res;
    } catch (err) {
      this.logError(err);
      interceptUnAuthenticatedError(err);

      if (axios.isAxiosError(err)) {
        if (err.response) {
          const typedError = err as RecordList;
          throw typedError.response?.data?.wfRes?.responseData?.errors?.[0] ||
            typedError.response?.data?.wfRes?.responseData ||
            new Error('An unexpected error occurred.');
        } else if (err.request) {
          throw new Error(
            'No response received from the server. Please check your network.'
          );
        } else {
          throw new Error('Error in request setup.');
        }
      } else {
        throw new Error('An unexpected error occurred.');
      }
    }
  }
}

export default Workflow;
